// STOVE Online Ordering - Standalone React App
// Main entry point that loads venue data and renders the ordering interface

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from './api/client';
import { Header } from './components/Header';
import { Menu } from './components/Menu';
import { Cart } from './components/Cart';
import { Checkout } from './components/Checkout';
import { CustomerModal } from './components/CustomerModal';
import { ItemModal } from './components/ItemModal';
import { OrderConfirmation, LoadingScreen, ErrorScreen } from './components/OrderConfirmation';
import { LocationSelector } from './components/LocationSelector';
import { ThemeProvider } from './context/ThemeContext';
import './styles/base.css';
import './styles/themes.css';

// Parse URL to get slug or venueid
// Supports: /tonys-pizza (slug), /35 (venueid), /?venueid=35, /?slug=tonys-pizza
function parseUrl() {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  
  // Check query params first
  if (params.get('venueid')) {
    return { type: 'venueid', value: params.get('venueid') };
  }
  if (params.get('slug')) {
    return { type: 'slug', value: params.get('slug') };
  }
  if (params.get('group')) {
    return { type: 'group', value: params.get('group') };
  }
  
  // Check path - remove leading slash and any trailing slashes
  const pathSegment = path.replace(/^\/+|\/+$/g, '').split('/')[0];
  
  if (!pathSegment) {
    return { type: 'none', value: null };
  }
  
  // If it's all digits, it's a venueid
  if (/^\d+$/.test(pathSegment)) {
    return { type: 'venueid', value: pathSegment };
  }
  
  // Otherwise it's a slug (could be venue or group)
  return { type: 'slug', value: pathSegment };
}

// Get theme from URL or default
function getThemeFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('theme') || 'classic';
}

// Get layout from URL or default
function getLayoutFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const validLayouts = ['classic', 'sidebar', 'scroll', 'bistro', 'compact'];
  const layout = params.get('layout');
  return validLayouts.includes(layout) ? layout : 'classic';
}

// Resolve "now" into the VENUE's local day-of-week + minutes-of-day so venue
// open/closed is evaluated in the venue's timezone, not the customer's browser.
// `tz` is an IANA name (e.g. 'America/Los_Angeles') from getOnlineData's
// venue.timezone. Falls back to browser-local time if tz is missing/invalid.
const DOW_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function venueNowParts(now, tz) {
  const d = now || new Date();
  if (tz) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).formatToParts(d);
      let wd = null, hh = null, mm = null;
      for (const p of parts) {
        if (p.type === 'weekday') wd = p.value;
        else if (p.type === 'hour') hh = parseInt(p.value, 10);
        else if (p.type === 'minute') mm = parseInt(p.value, 10);
      }
      if (hh === 24) hh = 0;
      if (wd && Number.isFinite(hh) && Number.isFinite(mm)) {
        return { today: wd, cur: hh * 100 + mm };
      }
    } catch (e) { /* bad tz -> fall through */ }
  }
  return { today: DOW_ABBR[d.getDay()], cur: d.getHours() * 100 + d.getMinutes() };
}

// Full lowercase day names, indexed to match Date.getDay() (0 = Sunday).
const DAY_NAMES_FULL = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Format an HHMM integer (e.g. 1130) as a friendly 12-hour time ("11:30 AM").
function formatHHMM(hhmm) {
  const h = Math.floor(hhmm / 100);
  const m = hhmm % 100;
  const ampm = h >= 12 ? 'PM' : 'AM';
  let hr = h % 12;
  if (hr === 0) hr = 12;
  return `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
}

// Normalize one day's schedule into an array of { open, close } HHMM windows,
// supporting SPLIT DAYS (e.g. lunch + dinner). Tolerant of several shapes:
//   { open:"1130", close:"1930", enabled:true }                       (single)
//   { open, close, enabled, open2, close2 }                           (two windows on one object)
//   [ { open, close, enabled }, { open, close, enabled } ]            (array of windows)
//   { enabled, windows:[ { open, close }, ... ] }                     (nested windows)
// A window is skipped if enabled === false or its times don't parse.
// Returns [] when the day is closed / has no valid windows.
function dayWindows(daySchedule) {
  if (!daySchedule) return [];

  const pushValid = (arr, o, c) => {
    const open = parseInt(o, 10);
    const close = parseInt(c, 10);
    if (!Number.isNaN(open) && !Number.isNaN(close)) arr.push({ open, close });
  };

  // Array of window objects.
  if (Array.isArray(daySchedule)) {
    const out = [];
    daySchedule.forEach(w => {
      if (w && w.enabled !== false) pushValid(out, w.open, w.close);
    });
    return out.sort((a, b) => a.open - b.open);
  }

  if (typeof daySchedule === 'object') {
    if (daySchedule.enabled === false) return [];

    // Nested windows array.
    if (Array.isArray(daySchedule.windows)) {
      const out = [];
      daySchedule.windows.forEach(w => {
        if (w && w.enabled !== false) pushValid(out, w.open, w.close);
      });
      return out.sort((a, b) => a.open - b.open);
    }

    // Flat object, optionally with a second window (open2/close2).
    const out = [];
    pushValid(out, daySchedule.open, daySchedule.close);
    pushValid(out, daySchedule.open2, daySchedule.close2);
    pushValid(out, daySchedule.open3, daySchedule.close3);
    return out.sort((a, b) => a.open - b.open);
  }

  return [];
}

// Is the given HHMM inside a window? Handles overnight windows (close < open).
function inWindow(cur, w) {
  return (w.open <= w.close) ? (cur >= w.open && cur < w.close)
                            : (cur >= w.open || cur < w.close);
}

// Given a day-keyed schedule ({ monday: { open:"1130", close, enabled }, ... })
// and the venue timezone, return a friendly label for the NEXT opening, e.g.
// "today at 11:30 AM", "tomorrow at 11:30 AM", "Monday at 11:30 AM".
// Returns null if no opening is found within the next 7 days.
function getNextOpenLabel(hours, tz) {
  if (!hours || typeof hours !== 'object') return null;

  // Venue-local current day index + time as an HHMM integer.
  let todayIdx, curHHMM;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, weekday: 'long', hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(new Date());
    let wd = null, hh = null, mm = null;
    for (const p of parts) {
      if (p.type === 'weekday') wd = p.value.toLowerCase();
      else if (p.type === 'hour') hh = parseInt(p.value, 10);
      else if (p.type === 'minute') mm = parseInt(p.value, 10);
    }
    if (hh === 24) hh = 0;
    todayIdx = DAY_NAMES_FULL.indexOf(wd);
    curHHMM = hh * 100 + mm;
  } catch (e) {
    const d = new Date();
    todayIdx = d.getDay();
    curHHMM = d.getHours() * 100 + d.getMinutes();
  }
  if (todayIdx < 0) return null;

  // Walk today + next 7 days; the first upcoming window start wins.
  for (let offset = 0; offset < 8; offset++) {
    const name = DAY_NAMES_FULL[(todayIdx + offset) % 7];
    const windows = dayWindows(hours[name]); // already sorted by open
    if (!windows.length) continue;

    // Today: the next window whose open is still ahead. Future days: the first window.
    const target = offset === 0
      ? windows.find(w => w.open > curHHMM)
      : windows[0];
    if (!target) continue;

    const when = offset === 0 ? 'today'
      : offset === 1 ? 'tomorrow'
      : name.charAt(0).toUpperCase() + name.slice(1);
    return `${when} at ${formatHHMM(target.open)}`;
  }
  return null;
}

// Build a human-readable weekly hours list from a day-keyed schedule.
// Returns [{ key, day, label, isToday }] ordered Monday -> Sunday, where label is
// e.g. "11:30 AM – 7:30 PM" or "Closed". `isToday` flags the venue-local day.
function buildWeeklyHours(hours, tz) {
  if (!hours || typeof hours !== 'object') return null;

  let todayIdx = -1;
  try {
    const wd = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long' })
      .format(new Date()).toLowerCase();
    todayIdx = DAY_NAMES_FULL.indexOf(wd);
  } catch (e) {
    todayIdx = new Date().getDay();
  }

  const order = [1, 2, 3, 4, 5, 6, 0]; // Mon, Tue, ... Sun
  return order.map(idx => {
    const name = DAY_NAMES_FULL[idx];
    const windows = dayWindows(hours[name]);
    const label = windows.length
      ? windows.map(w => `${formatHHMM(w.open)} – ${formatHHMM(w.close)}`).join(', ')
      : 'Closed';
    return {
      key: name,
      day: name.charAt(0).toUpperCase() + name.slice(1),
      label,
      isToday: idx === todayIdx
    };
  });
}

export default function App() {
  // Core state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [venueData, setVenueData] = useState(null);
  const [menuData, setMenuData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [venueid, setVenueid] = useState(null);
  
  // Restaurant group state (for location selection)
  const [restaurantGroup, setRestaurantGroup] = useState(null);
  const [groupVenues, setGroupVenues] = useState([]);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  
  // UI state
  const [theme, setTheme] = useState(getThemeFromUrl());
  const [layout, setLayout] = useState(getLayoutFromUrl()); // classic, sidebar, scroll, bistro, compact
  const [view, setView] = useState('menu'); // menu, cart, checkout, confirmation
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [editingCartItem, setEditingCartItem] = useState(null); // Cart item being edited
  const [showWeekHours, setShowWeekHours] = useState(false); // expand weekly hours in closed modal
  const [hoursModalOpen, setHoursModalOpen] = useState(false); // standalone "store hours" modal

  // Cart state
  const [cart, setCart] = useState([]);
  const [orderType, setOrderType] = useState(null);
  const [availableOrderTypes, setAvailableOrderTypes] = useState([]); // Online-enabled order types
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [scheduledTime, setScheduledTime] = useState(null);
  const [promoCode, setPromoCode] = useState(null);
  const [promoDiscount, setPromoDiscount] = useState(0);
  
  // Customer state
  const [customer, setCustomer] = useState(null);
  
  // Order state
  const [completedOrder, setCompletedOrder] = useState(null);
  
  // Resolve URL and load initial data
  useEffect(() => {
    async function resolveAndLoad() {
      try {
        setLoading(true);
        setError(null);
        
        const urlInfo = parseUrl();
        
        if (urlInfo.type === 'none') {
          throw new Error('No venue or restaurant group specified in URL');
        }
        
        // Resolve slug to venue or group
        if (urlInfo.type === 'slug') {
          const resolved = await api.resolveSlug(urlInfo.value);
          
          if (!resolved.result) {
            throw new Error(resolved.message || 'Invalid URL');
          }
          
          if (resolved.type === 'group') {
            // It's a restaurant group - show location selector
            setRestaurantGroup(resolved.group);
            setGroupVenues(resolved.venues);
            setShowLocationSelector(true);
            
            // Apply group theme if set
            if (resolved.group?.theme) {
              setTheme(resolved.group.theme);
            }
            
            setLoading(false);
            return;
          } else if (resolved.type === 'venue') {
            // It's a single venue - load it
            setVenueid(resolved.venueid);
            await loadVenueData(resolved.venueid);
          }
        } else if (urlInfo.type === 'venueid') {
          setVenueid(urlInfo.value);
          await loadVenueData(urlInfo.value);
        } else if (urlInfo.type === 'group') {
          // Direct group access
          const groupData = await api.getRestaurantGroup(urlInfo.value);
          if (!groupData.result) {
            throw new Error(groupData.message || 'Restaurant group not found');
          }
          setRestaurantGroup(groupData.group);
          setGroupVenues(groupData.venues);
          setShowLocationSelector(true);
          setLoading(false);
          return;
        }
        
      } catch (err) {
        console.error('Load error:', err);
        setError(err.message);
        setLoading(false);
      }
    }
    
    resolveAndLoad();
  }, []);

  // Load venue data helper
  async function loadVenueData(vid) {
    try {
      setLoading(true);

      // Load venue data (settings, order types, etc.)
      const venueResponse = await api.getOnlineData(vid);
      if (!venueResponse.result) {
        throw new Error(venueResponse.message || 'Failed to load venue');
      }
      setVenueData(venueResponse.venue);
      setSettings(venueResponse.onlineSettings);
      
      // Check if venue is part of a restaurant group - save for potential "change location" feature
      if (venueResponse.venue?.restaurant_group_id) {
        const groupData = await api.getRestaurantGroup(venueResponse.venue.restaurant_group_id);
        if (groupData.result) {
          setRestaurantGroup(groupData.group);
          setGroupVenues(groupData.venues);
        }
      }

      // Load menu data
      const menuResponse = await api.getMenu(vid);
      if (!menuResponse.result) {
        throw new Error(menuResponse.message || 'Failed to load menu');
      }
      // Resolve each item's regular online image from itemImages_json.
      // That column is a JSON object keyed by surface, e.g.
      //   { online, onlinefeatured, kiosk, kioskfeatured }
      // The regular item list uses ONLY the 'online' image. There is no fallback:
      // if 'online' is absent, no image (and no placeholder) is shown.
      // 'onlinefeatured' is reserved for the larger featured-item box (separate feature).
      const resolveItemImage = (raw) => {
        if (!raw) return '';
        let imgs = raw;
        if (typeof raw === 'string') {
          try { imgs = JSON.parse(raw); } catch (e) { return ''; }
        }
        if (!imgs || typeof imgs !== 'object') return '';
        const url = imgs.online || '';
        return typeof url === 'string' ? url.trim() : '';
      };

      const menuItemsWithImages = (menuResponse.menuItems || []).map(it => ({
        ...it,
        // keep any pre-set image_url, otherwise derive it from itemImages_json
        image_url: (it.image_url && it.image_url.trim()) || resolveItemImage(it.itemImages_json)
      }));

      setMenuData({
        categories: menuResponse.menuCat || [],
        items: menuItemsWithImages,
        // Reference targets for itemOptions_json `{type:"reference", dataSource}` entries.
        // These live in the getMenu `menuGroup` array, keyed by `recId`.
        // NOTE: this is NOT menuAltMods/menugroupmod_data — that's a separate object
        // with a different shape that online ordering does not use.
        menuGroups: menuResponse.menuGroup || [],
        itemClasses: menuResponse.menuClass || []
      });
      
      // Select first category
      if (menuResponse.menuCat?.length > 0) {
        setSelectedCategory(menuResponse.menuCat[0].menucat);
      }

      // Set default order type based on onlineSettings.
      // Primary schema (what this venue uses):
      //   onlineSettings.enabledOrderTypes -> array of numeric order-type ids, e.g. [3, 4, 2]
      //   onlineSettings.defaultOrderType  -> the id to pre-select, e.g. "2"
      // Legacy fallback schema (older venues): per-type keys takeoutOrderType / deliveryOrderType / etc.
      if (venueResponse.orderTypes?.length > 0) {
        const onlineSettings = venueResponse.onlineSettings || {};
        const orderTypes = venueResponse.orderTypes;

        // Helper to find an order type by id - tolerates string/number and id/ordertypeid.
        // Returns null for null/undefined/empty/0 so unconfigured slots don't match.
        const findOrderType = (id) => {
          if (id === null || id === undefined || id === '' || id === 0 || id === '0') {
            return null;
          }
          return orderTypes.find(ot =>
            ot.id === id ||
            ot.id === String(id) ||
            String(ot.id) === String(id) ||
            ot.ordertypeid === id ||
            ot.ordertypeid === String(id) ||
            String(ot.ordertypeid) === String(id)
          ) || null;
        };

        // Derive the _onlineType tag from the type name. Only 'delivery' changes
        // behavior downstream (shows the address fields); the rest are cosmetic.
        const onlineTypeFromName = (name) => {
          const n = String(name || '').toLowerCase().replace(/[^a-z]/g, '');
          if (n.includes('delivery')) return 'delivery';
          if (n.includes('takeout') || n.includes('takeaway') || n.includes('pickup')) return 'takeout';
          if (n.includes('roomservice')) return 'roomservice';
          if (n.includes('curbside')) return 'curbside';
          if (n.includes('dine')) return 'dinein';
          return n || 'takeout';
        };

        let availableOnlineTypes = [];

        // 1) Primary: enabledOrderTypes (array of ids), preserving configured order.
        if (Array.isArray(onlineSettings.enabledOrderTypes) && onlineSettings.enabledOrderTypes.length > 0) {
          availableOnlineTypes = onlineSettings.enabledOrderTypes
            .map(id => findOrderType(id))
            .filter(Boolean)
            .map(ot => ({ ...ot, _onlineType: onlineTypeFromName(ot.name || ot.order_type) }));
        }

        // 2) Legacy fallback: per-type keys, for venues still on the old schema.
        if (availableOnlineTypes.length === 0) {
          const legacy = [
            [findOrderType(onlineSettings.takeoutOrderType || onlineSettings.takoutOrderType), 'takeout'],
            [findOrderType(onlineSettings.deliveryOrderType), 'delivery'],
            [findOrderType(onlineSettings.dineinOrderType), 'dinein'],
            [findOrderType(onlineSettings.roomserviceOrderType), 'roomservice'],
          ];
          availableOnlineTypes = legacy
            .filter(([ot]) => ot)
            .map(([ot, tag]) => ({ ...ot, _onlineType: tag }));
        }

        // 3) Last resort: a numeric synthetic Takeout. NEVER a string id - a string
        //    id is what was being POSTed as order_type_id and stored as 0.
        if (availableOnlineTypes.length === 0) {
          availableOnlineTypes = [{ id: 4, name: 'Takeout', order_type: 'Takeout', _onlineType: 'takeout' }];
        }

        // Store available types for the order type selector
        setAvailableOrderTypes(availableOnlineTypes);

        // Initial selection: configured defaultOrderType (if it's in the available list),
        // then takeout, then delivery, then the first available type.
        const configuredDefault = findOrderType(onlineSettings.defaultOrderType);
        const defaultId = configuredDefault ? (configuredDefault.id ?? configuredDefault.ordertypeid) : null;
        const defaultType =
          (defaultId != null && availableOnlineTypes.find(t => String(t.id ?? t.ordertypeid) === String(defaultId))) ||
          availableOnlineTypes.find(t => t._onlineType === 'takeout') ||
          availableOnlineTypes.find(t => t._onlineType === 'delivery') ||
          availableOnlineTypes[0];
        setOrderType(defaultType);

        // Log for debugging
        console.log('Order types configured:', {
          fromSettings: {
            enabled: onlineSettings.enabledOrderTypes,
            default: onlineSettings.defaultOrderType
          },
          available: availableOnlineTypes.map(t => ({
            id: t.id ?? t.ordertypeid,
            name: t.name || t.order_type,
            type: t._onlineType
          })),
          selected: defaultType ? (defaultType.name || defaultType.order_type) : 'none'
        });
      }

      // Payment methods offered in online checkout, derived from onlineSettings.
      // For each method: honor an explicit boolean flag if the venue set one;
      // otherwise fall back to "is it configured?" — i.e. the presence of the
      // relevant API key / merchant id that already lives in onlineSettings.
      const os = venueResponse.onlineSettings || {};
      const paymentSettings = {
        // Baseline — the CC key is resolved server-side in processOnlinePayment
        // (onlineSettings.creditCardPaymentApiKey || globalSettings.paymentCreditCardAPIKey).
        creditCard: true,
        giftCard:  (os.giftCardOnline  != null) ? !!os.giftCardOnline
                                                 : !!os.giftCardPaymentApiKey,
        applePay:  (os.acceptApplePay  != null) ? !!os.acceptApplePay
                                                 : (!!os.applePay  || !!os.applePayMerchantId),
        googlePay: (os.acceptGooglePay != null) ? !!os.acceptGooglePay
                                                 : (!!os.googlePay || !!os.googlePayMerchantId)
      };
      setSettings(prev => ({ ...prev, paymentSettings }));

      // ---- Open/closed hours config -------------------------------------
      // The actual schedules live in globalSettings (keyed by day name, with
      // "1130"-style HHMM open/close + an enabled flag). onlineSettings only
      // carries the useVenueHours toggle and temporarilyClosed. Capture both
      // from wherever getOnlineData surfaces them so isVenueOpen can use them.
      const gs = venueResponse.defaultSettings?.globalSettings
              || venueResponse.globalSettings
              || {};
      const hoursConfig = {
        useVenueHours: (os.useVenueHours != null) ? !!os.useVenueHours
                       : (gs.useVenueHours != null ? !!gs.useVenueHours : false),
        businessHours: gs.businessHours || os.businessHours || null,
        onlineHours:   gs.onlineHours   || os.onlineHours   || null,
        temporarilyClosed: !!(os.temporarilyClosed ?? gs.temporarilyClosed),
        timezone: gs.timezonename || venueResponse.venue?.timezone || os.timezonename || null
      };
      // Debug: if businessHours/onlineHours are null here, getOnlineData isn't
      // returning globalSettings and the backend must include it.
      console.info('Venue hours config:', hoursConfig);
      setSettings(prev => ({ ...prev, hoursConfig }));


      // Check for theme in settings (URL param takes precedence)
      const urlTheme = new URLSearchParams(window.location.search).get('theme');
      if (!urlTheme && venueResponse.onlineSettings?.theme) {
        setTheme(venueResponse.onlineSettings.theme);
      }
      
      // Check for layout in settings (URL param takes precedence)
      const urlLayout = new URLSearchParams(window.location.search).get('layout');
      if (!urlLayout && venueResponse.onlineSettings?.layout) {
        const validLayouts = ['classic', 'sidebar', 'scroll', 'bistro', 'compact'];
        if (validLayouts.includes(venueResponse.onlineSettings.layout)) {
          setLayout(venueResponse.onlineSettings.layout);
        }
      }

      setShowLocationSelector(false);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load venue data:', err);
      setError(err.message || 'Failed to load restaurant data');
      setLoading(false);
    }
  }

  // Handle venue selection from LocationSelector
  const handleVenueSelect = useCallback(async (venue) => {
    setVenueid(venue.venueid);
    // Update URL without reload
    const newUrl = `/${venue.slug || venue.venueid}`;
    window.history.pushState({}, '', newUrl);
    await loadVenueData(venue.venueid);
  }, []);

  // Handle "find closest" - uses geolocation
  const handleFindClosest = useCallback(async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }
    
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000
        });
      });
      
      const { latitude, longitude } = position.coords;
      
      // Find closest venue
      let closestVenue = null;
      let closestDistance = Infinity;
      
      groupVenues.forEach(venue => {
        if (venue.latitude && venue.longitude) {
          const dist = getDistance(latitude, longitude, venue.latitude, venue.longitude);
          if (dist < closestDistance) {
            closestDistance = dist;
            closestVenue = venue;
          }
        }
      });
      
      if (closestVenue) {
        handleVenueSelect(closestVenue);
      } else {
        alert('Could not determine closest location. Please select manually.');
      }
    } catch (err) {
      console.error('Geolocation error:', err);
      alert('Could not get your location. Please select a location manually.');
    }
  }, [groupVenues, handleVenueSelect]);

  // Haversine distance formula
  function getDistance(lat1, lon1, lat2, lon2) {
    const R = 3959; // miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  // Cart functions
  const addToCart = useCallback((cartItemOrItem, modifiers = [], quantity = 1, specialInstructions = '') => {
    // Closed venues can still take SCHEDULED orders, so adding to the cart is
    // always allowed. The "must schedule when closed" rule is enforced at checkout.
    let cartItem;
    
    // Check if we received a pre-built cart item (from ItemModal)
    if (cartItemOrItem.lineTotal !== undefined && cartItemOrItem.modifierTotal !== undefined) {
      // It's a complete cart item - just add an id if needed
      cartItem = {
        ...cartItemOrItem,
        id: cartItemOrItem.id || (Date.now() + Math.random())
      };
    } else {
      // Legacy format: (item, modifiers, quantity, specialInstructions)
      const item = cartItemOrItem;
      cartItem = {
        id: Date.now() + Math.random(),
        itemid: item.itemid,
        name: item.item_name,
        price: parseFloat(item.item_price),
        quantity,
        modifiers,
        specialInstructions,
        taxrate: parseFloat(item.taxrate) || 0
      };
      
      // Calculate modifier prices
      let modifierTotal = 0;
      modifiers.forEach(mod => {
        if (mod.price) {
          modifierTotal += parseFloat(mod.price);
        }
      });
      cartItem.modifierTotal = modifierTotal;
      cartItem.lineTotal = (cartItem.price + modifierTotal) * quantity;
    }
    
    setCart(prev => [...prev, cartItem]);
    setCartOpen(true);
  }, []);

  const updateCartItem = useCallback((cartItemId, updates) => {
    setCart(prev => {
      // If quantity update would result in 0 or less, remove the item
      if (updates.quantity !== undefined && updates.quantity <= 0) {
        return prev.filter(item => item.id !== cartItemId);
      }
      
      return prev.map(item => {
        if (item.id === cartItemId) {
          const updated = { ...item, ...updates };
          const price = parseFloat(updated.price) || 0;
          const modTotal = parseFloat(updated.modifierTotal) || 0;
          const qty = parseInt(updated.quantity) || 1;
          updated.lineTotal = (price + modTotal) * qty;
          // Keep the edit snapshot's qty in sync with the cart stepper, so
          // re-opening the editor shows the current quantity, not the original.
          if (updates.quantity !== undefined && updated._editMeta) {
            updated._editMeta = { ...updated._editMeta, qty };
          }
          return updated;
        }
        return item;
      });
    });
  }, []);

  const removeFromCart = useCallback((cartItemId) => {
    setCart(prev => prev.filter(item => item.id !== cartItemId));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    setPromoCode(null);
    setPromoDiscount(0);
  }, []);

  // Edit cart item - opens the item modal with existing selections
  const editCartItem = useCallback((cartItem) => {
    if (!cartItem?._editMeta?.item) return;
    setEditingCartItem(cartItem);
    setCartOpen(false); // Close cart drawer when editing
  }, []);

  // Handle edited item save - replaces the old item
  const handleEditedItemSave = useCallback((newCartItem) => {
    if (!editingCartItem) return;
    
    setCart(prev => prev.map(item => 
      item.id === editingCartItem.id ? { ...newCartItem, id: editingCartItem.id } : item
    ));
    setEditingCartItem(null);
  }, [editingCartItem]);

  // Calculate totals (with NaN protection)
  const cartTotals = React.useMemo(() => {
    const safeNum = (v) => {
      const n = parseFloat(v);
      return isNaN(n) ? 0 : n;
    };
    
    const subtotal = cart.reduce((sum, item) => sum + safeNum(item.lineTotal), 0);
    const taxRate = cart.length > 0 ? safeNum(cart[0].taxrate) || 0.0825 : 0.0825;
    // Round tax to the nearest cent (never truncate). Building the total from the
    // rounded tax keeps total === subtotal + tax − discount to the penny, so the
    // charged amount can't land $0.01 under the order total (partial-payment bug).
    const tax = Math.round(subtotal * taxRate * 100) / 100;
    const discount = safeNum(promoDiscount) || 0;
    const total = Math.round((subtotal + tax - discount) * 100) / 100;
    
    return {
      subtotal,
      tax,
      taxRate,
      discount,
      total,
      itemCount: cart.reduce((sum, item) => sum + safeNum(item.quantity), 0)
    };
  }, [cart, promoDiscount]);

  // Apply promo code
  const applyPromoCode = useCallback(async (code) => {
    try {
      const response = await api.validatePromo(venueid, code, cartTotals.subtotal);
      if (response.result === 1) {
        setPromoCode(code);
        setPromoDiscount(parseFloat(response.value) || 0);
        return { success: true, message: response.reason };
      } else {
        return { success: false, message: response.reason || 'Invalid code' };
      }
    } catch (err) {
      return { success: false, message: 'Failed to validate code' };
    }
  }, [venueid, cartTotals.subtotal]);

  // Handle checkout completion
  const handleOrderComplete = useCallback((orderData) => {
    setCompletedOrder(orderData);
    setView('confirmation');
    clearCart();
  }, [clearCart]);

  // Handle starting new order
  const handleNewOrder = useCallback(() => {
    setCompletedOrder(null);
    setView('menu');
    setCustomer(null);
  }, []);

  // Check if the venue is currently open for online ordering, evaluated in the
  // venue's timezone. Uses businessHours when useVenueHours is on, else
  // onlineHours. Each schedule is keyed by lowercase day name:
  //   { monday: { open: "1130", close: "1930", enabled: true }, ... }
  const isVenueOpen = React.useMemo(() => {
    const hc = settings?.hoursConfig || {};

    // Manual override always wins.
    if (settings?.temporarilyClosed || hc.temporarilyClosed) return false;

    // Pick the active schedule; fall back to the other if the chosen one is absent.
    const chosen = hc.useVenueHours ? hc.businessHours : hc.onlineHours;
    const hours = (chosen && typeof chosen === 'object')
      ? chosen
      : (hc.onlineHours || hc.businessHours);

    // No hours configured at all -> default open (don't block on missing data).
    if (!hours || typeof hours !== 'object') return true;

    const tz = hc.timezone || venueData?.timezone;

    // Venue-local lowercase day name + current time as an HHMM integer.
    let dayName, curHHMM;
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, weekday: 'long', hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(new Date());
      let wd = null, hh = null, mm = null;
      for (const p of parts) {
        if (p.type === 'weekday') wd = p.value.toLowerCase();
        else if (p.type === 'hour') hh = parseInt(p.value, 10);
        else if (p.type === 'minute') mm = parseInt(p.value, 10);
      }
      if (hh === 24) hh = 0;
      dayName = wd;
      curHHMM = hh * 100 + mm;
    } catch (e) {
      const d = new Date();
      dayName = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][d.getDay()];
      curHHMM = d.getHours() * 100 + d.getMinutes();
    }

    const windows = dayWindows(hours[dayName]);
    if (!windows.length) return false; // no windows today (closed / disabled)

    // Open if the current time falls inside ANY of the day's windows (split days).
    return windows.some(w => inWindow(curHHMM, w));
  }, [settings, venueData]);

  // Message shown when closed (from onlineSettings.closedMessage), with a fallback.
  const closedMessage = settings?.closedMessage
    || 'We are currently not accepting online orders. Please check back during our hours.';

  // When closed, work out when we open next (based on the active schedule).
  const nextOpenLabel = React.useMemo(() => {
    if (isVenueOpen) return null;
    const hc = settings?.hoursConfig || {};
    const chosen = hc.useVenueHours ? hc.businessHours : hc.onlineHours;
    const hours = (chosen && typeof chosen === 'object') ? chosen : (hc.onlineHours || hc.businessHours);
    return getNextOpenLabel(hours, hc.timezone || venueData?.timezone);
  }, [isVenueOpen, settings, venueData]);

  // Full weekly hours (human-readable), for the "view all hours" expander.
  const weeklyHours = React.useMemo(() => {
    const hc = settings?.hoursConfig || {};
    const chosen = hc.useVenueHours ? hc.businessHours : hc.onlineHours;
    const hours = (chosen && typeof chosen === 'object') ? chosen : (hc.onlineHours || hc.businessHours);
    return buildWeeklyHours(hours, hc.timezone || venueData?.timezone);
  }, [settings, venueData]);

  // Loading state
  if (loading) {
    return <LoadingScreen theme={theme} />;
  }

  // Error state
  if (error) {
    return <ErrorScreen message={error} onRetry={() => window.location.reload()} />;
  }

  // Restaurant group - show location selector
  if (showLocationSelector) {
    return (
      <ThemeProvider theme={theme} settings={restaurantGroup}>
        <div
          className={`app theme--${theme}`}
          style={getCustomStyles(restaurantGroup && restaurantGroup.styleSettings)}
        >
          <LocationSelector
            group={restaurantGroup}
            venues={groupVenues}
            onSelectVenue={handleVenueSelect}
            onFindClosest={handleFindClosest}
          />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme} settings={settings}>
      <div className={`app theme--${theme}`} style={getCustomStyles(settings)}>
        <Header 
          venue={venueData}
          settings={settings}
          cartCount={cartTotals.itemCount}
          onCartClick={() => setCartOpen(true)}
          isOpen={isVenueOpen}
          showChangeLocation={groupVenues.length > 1}
          onChangeLocation={() => setShowLocationSelector(true)}
        />

        <main className="main-content">
          {view === 'menu' && !isVenueOpen && (
            <div
              className="venue-closed-banner"
              role="alert"
              style={{
                background: '#b91c1c',
                color: '#fff',
                padding: '14px 16px',
                textAlign: 'center',
                fontWeight: 600,
                lineHeight: 1.4
              }}
            >
              {closedMessage}
              {nextOpenLabel && (
                <span style={{ display: 'block', fontWeight: 500, marginTop: 4 }}>
                  We open {nextOpenLabel}. You can still schedule an order for later.
                </span>
              )}
              {weeklyHours && (
                <button
                  type="button"
                  onClick={() => setHoursModalOpen(true)}
                  style={{
                    display: 'inline-block', marginTop: 8, background: 'none', border: 'none',
                    color: '#fff', textDecoration: 'underline', cursor: 'pointer',
                    fontWeight: 500, fontSize: '0.9rem'
                  }}
                >
                  View store hours
                </button>
              )}
            </div>
          )}

          {view === 'menu' && isVenueOpen && weeklyHours && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 12px' }}>
              <button
                type="button"
                onClick={() => setHoursModalOpen(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none',
                  border: 'none', cursor: 'pointer', color: 'var(--text, #374151)',
                  fontSize: '0.9rem', opacity: 0.85
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                View store hours
              </button>
            </div>
          )}

          {view === 'menu' && (
            <Menu
              categories={menuData.categories}
              items={menuData.items}
              menuGroups={menuData.menuGroups}
              selectedCategory={selectedCategory}
              onCategorySelect={setSelectedCategory}
              onAddToCart={addToCart}
              settings={settings}
              timezone={venueData?.timezone}
              layout={layout}
            />
          )}

          {view === 'checkout' && (
            <Checkout
              venueid={venueid}
              cart={cart}
              cartTotals={cartTotals}
              customer={customer}
              orderType={orderType}
              availableOrderTypes={availableOrderTypes}
              deliveryAddress={deliveryAddress}
              scheduledTime={scheduledTime}
              venueOpen={isVenueOpen}
              promoCode={promoCode}
              settings={settings}
              venueData={venueData}
              onBack={() => setView('menu')}
              onCustomerEdit={() => setCustomerModalOpen(true)}
              onOrderTypeChange={setOrderType}
              onDeliveryAddressChange={setDeliveryAddress}
              onScheduleChange={setScheduledTime}
              onApplyPromo={applyPromoCode}
              onOrderComplete={handleOrderComplete}
            />
          )}

          {view === 'confirmation' && completedOrder && (
            <OrderConfirmation
              order={completedOrder}
              venue={venueData}
              settings={settings}
              onNewOrder={handleNewOrder}
            />
          )}
        </main>

        {/* Cart Drawer */}
        <Cart
          open={cartOpen}
          cart={cart}
          totals={cartTotals}
          orderType={orderType}
          availableOrderTypes={availableOrderTypes}
          deliveryAddress={deliveryAddress}
          onOrderTypeChange={setOrderType}
          onDeliveryAddressChange={setDeliveryAddress}
          onClose={() => setCartOpen(false)}
          onUpdateItem={updateCartItem}
          onRemoveItem={removeFromCart}
          onEditItem={editCartItem}
          onClearCart={clearCart}
          onCheckout={() => {
            // Closed venues proceed to checkout too — Checkout requires a
            // scheduled time when the venue isn't open now.
            setCartOpen(false);
            if (!customer) {
              setCustomerModalOpen(true);
            } else {
              setView('checkout');
            }
          }}
          settings={settings}
        />

        {/* Customer Modal */}
        <CustomerModal
          open={customerModalOpen}
          venueid={venueid}
          onClose={() => setCustomerModalOpen(false)}
          onCustomerFound={(c) => {
            setCustomer(c);
            setCustomerModalOpen(false);
            setView('checkout');
          }}
          onGuestCheckout={(guestInfo) => {
            setCustomer({ ...guestInfo, isGuest: true });
            setCustomerModalOpen(false);
            setView('checkout');
          }}
          settings={settings}
        />

        {/* Edit Item Modal */}
        {editingCartItem && editingCartItem._editMeta?.item && (
          <ItemModal
            open={!!editingCartItem}
            item={editingCartItem._editMeta.item}
            modGroups={menuData.menuGroups}
            editMeta={{
              ...editingCartItem._editMeta,
              // Use the cart line's live quantity (it may have changed via the
              // cart stepper after the item was added), not the stale snapshot.
              qty: editingCartItem.quantity ?? editingCartItem._editMeta.qty ?? 1
            }}
            onClose={() => setEditingCartItem(null)}
            onAdd={handleEditedItemSave}
          />
        )}

        {/* Store Hours Modal - reachable anytime, open or closed */}
        {hoursModalOpen && weeklyHours && (
          <div className="modal-overlay" onClick={() => setHoursModalOpen(false)}>
            <div
              className="modal"
              role="dialog"
              aria-modal="true"
              onClick={e => e.stopPropagation()}
              style={{
                maxWidth: 420, width: '90%', padding: '28px 28px 24px',
                borderRadius: 16, background: 'var(--bg, #fff)'
              }}
            >
              <h2 style={{ margin: '0 0 4px', fontSize: '1.25rem', textAlign: 'center' }}>Store Hours</h2>
              <p style={{ margin: '0 0 16px', textAlign: 'center', color: isVenueOpen ? '#15803d' : '#b91c1c', fontWeight: 600 }}>
                {isVenueOpen ? 'Open now' : (nextOpenLabel ? `Closed \u2022 opens ${nextOpenLabel}` : 'Closed')}
              </p>
              <div style={{ fontSize: '0.95rem', borderTop: '1px solid var(--border, #e5e7eb)', paddingTop: 12 }}>
                {weeklyHours.map(d => (
                  <div
                    key={d.key}
                    style={{
                      display: 'flex', justifyContent: 'space-between', gap: 16, padding: '5px 0',
                      fontWeight: d.isToday ? 700 : 400,
                      color: d.label === 'Closed' ? 'var(--muted, #9ca3af)' : 'inherit'
                    }}
                  >
                    <span>{d.day}{d.isToday ? ' (today)' : ''}</span>
                    <span style={{ textAlign: 'right' }}>{d.label}</span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setHoursModalOpen(false)}
                style={{
                  marginTop: 20, width: '100%', padding: '12px', borderRadius: 10, border: 'none',
                  cursor: 'pointer', background: 'var(--primary, #b91c1c)', color: '#fff',
                  fontWeight: 600, fontSize: '1rem'
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

// Generate custom CSS variables from settings
function getCustomStyles(settings) {
  if (!settings) return {};
  
  const styles = {};
  
  if (settings.primaryColor) {
    styles['--primary'] = settings.primaryColor;
  }
  if (settings.secondaryColor) {
    styles['--secondary'] = settings.secondaryColor;
  }
  if (settings.accentColor) {
    styles['--accent'] = settings.accentColor;
  }
  if (settings.backgroundColor) {
    styles['--bg'] = settings.backgroundColor;
  }
  if (settings.textColor) {
    styles['--text'] = settings.textColor;
  }
  if (settings.fontFamily) {
    styles['--font'] = settings.fontFamily;
  }
  
  return styles;
}